// @ts-check
/** @typedef {import('../types/api').VendorOrder} VendorOrder */
/** @typedef {import('../types/api').Vendor} Vendor */

import { Building2, Calendar, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import vendorService from '../services/vendorService';

const VendorOrdersPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  /** @type {[VendorOrder[], Function]} */
  const [orders, setOrders] = useState([]);
  /** @type {[Vendor[], Function]} */
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    vendor_id: '',
    start_date: '',
    end_date: '',
  });

  const orderStatuses = [
    { value: 'draft', label: 'Draft', color: 'bg-muted text-foreground' },
    { value: 'sent', label: 'Sent', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300' },
    {
      value: 'confirmed',
      label: 'Confirmed',
      color: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-300',
    },
    {
      value: 'in_production',
      label: 'In Production',
      color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
    },
    {
      value: 'ready_for_pickup',
      label: 'Ready',
      color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    },
    {
      value: 'delivered',
      label: 'Delivered',
      color: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
    },
    {
      value: 'completed',
      label: 'Completed',
      color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [ordersData, vendorsData] = await Promise.all([
        vendorService.getVendorOrders(filters),
        vendorService.getVendors({ status: 'active' }),
      ]);
      setOrders(ordersData);
      setVendors(vendorsData);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getVendorName = (vendorId) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    return vendor ? vendor.vendor_name : 'Unknown';
  };

  const groupedOrders = orderStatuses.reduce((acc, status) => {
    acc[status.value] = orders.filter((order) => order.status === status.value);
    return acc;
  }, {});

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await vendorService.updateVendorOrder(orderId, { status: newStatus });
      showToast('Order status updated', 'success');
      fetchData();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  return (
    <div className="bg-muted/50">
      {/* Header - Sticky */}
      <div className="sticky top-0 z-20 bg-card border-b border-border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Vendor Orders</h1>
            <p className="text-muted-foreground mt-1">Track and manage vendor orders and jobs</p>
          </div>
          <button
            onClick={() => navigate('/vendors/orders/create')}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition flex-shrink-0"
          >
            <Plus size={20} />
            Create Order
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Filters */}
        <div className="bg-card rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">
                <Building2 size={16} className="inline mr-1" />
                Filter by Vendor
              </label>
              <select
                value={filters.vendor_id}
                onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              >
                <option value="">All Vendors</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.vendor_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">
                <Calendar size={16} className="inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">
                <Calendar size={16} className="inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Kanban Board - Horizontal Scroll Container */}
        {loading ? (
          <LoadingScreen />
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {orderStatuses.map((statusConfig) => {
                const statusOrders = groupedOrders[statusConfig.value] || [];

                return (
                  <div key={statusConfig.value} className="flex-shrink-0 w-80">
                    <div className="bg-card rounded-lg shadow">
                      <div className="p-4 border-b">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <h3 className="font-semibold text-foreground">{statusConfig.label}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
                            {statusOrders.length}
                          </span>
                        </div>
                      </div>

                      <div className="p-2 space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
                        {statusOrders.length === 0 ? (
                          <p className="text-center text-muted-foreground/70 py-8 text-sm">No orders</p>
                        ) : (
                          statusOrders.map((order) => (
                            <div
                              key={order.id}
                              className="bg-muted/50 rounded-lg p-4 border border-border hover:shadow-md transition cursor-pointer"
                              onClick={() => navigate(`/vendors/orders/${order.id}`)}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-mono text-sm font-semibold text-foreground">
                                  {order.order_number}
                                </span>
                                <span
                                  className={`text-xs px-2 py-1 rounded-full ${
                                    order.priority === 'urgent'
                                      ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                                      : order.priority === 'high'
                                        ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
                                        : order.priority === 'medium'
                                          ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                                          : 'bg-muted text-foreground'
                                  }`}
                                >
                                  {order.priority}
                                </span>
                              </div>

                              <p className="text-sm font-medium text-foreground mb-2">
                                {getVendorName(order.vendor_id)}
                              </p>

                              <div className="flex justify-between items-center text-xs text-muted-foreground mb-3">
                                <span>₹{parseFloat(order.total_amount).toLocaleString('en-IN')}</span>
                                <span>{new Date(order.order_date).toLocaleDateString()}</span>
                              </div>

                              {order.expected_delivery_date && (
                                <p className="text-xs text-muted-foreground">
                                  📅 Due: {new Date(order.expected_delivery_date).toLocaleDateString()}
                                </p>
                              )}

                              {/* Status Change Dropdown */}
                              <div className="mt-3 pt-3 border-t">
                                <select
                                  value={order.status}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(order.id, e.target.value);
                                  }}
                                  className="w-full text-xs px-2 py-1 border border-input rounded-md focus:ring-1 focus:ring-foreground"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {orderStatuses.map((s) => (
                                    <option key={s.value} value={s.value}>
                                      {s.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorOrdersPage;
